/**
 * Top-up payments.
 *
 * The three columns that matter sit next to each other on purpose: what the
 * customer PAID, what we gave as BONUS, and what the wallet was CREDITED. A
 * report that showed only the credited figure would overstate revenue by the
 * whole promotional budget.
 */
import { useCallback, useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'

import EmptyState from '../../components/shared/EmptyState'
import LoadErrorState from '../../components/common/LoadErrorState'
import {
  Card,
  Chip,
  Field,
  PillButton,
  SelectInput,
  StatCard,
  StatRow,
  TableHead,
  Td,
} from '../../features/b2b/components/primitives'
import { useColors } from '../../hooks/useColors'
import { formatINR, getTopups } from '../../services/walletApi'
import type { DateRangeFilter, TopupRecord } from '../../services/walletApi'

const PAGE_SIZE = 25

const COLUMNS = [
  { key: 'customer', label: 'Customer' },
  { key: 'offer', label: 'Offer' },
  { key: 'paid', label: 'Customer paid', align: 'right' as const },
  { key: 'bonus', label: 'Bonus', align: 'right' as const },
  { key: 'credited', label: 'Wallet credited', align: 'right' as const },
  { key: 'status', label: 'Status' },
  { key: 'when', label: 'When' },
]

const STATUS_TONE = {
  paid: 'success',
  failed: 'danger',
  created: 'warning',
} as const

const STATUS_LABEL = {
  paid: 'Paid',
  failed: 'Failed',
  created: 'Started',
} as const

export default function TopupsTab({ filter }: { filter: DateRangeFilter }) {
  const colors = useColors()
  const [rows, setRows] = useState<TopupRecord[]>([])
  const [total, setTotal] = useState(0)
  const [totals, setTotals] = useState({
    revenue_collected: 0, promotional_bonus_issued: 0, wallet_credited: 0, successful: 0, failed: 0,
  })
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setPage(1)
  }, [filter, status])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result = await getTopups({
        ...filter,
        status: status || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      })
      setRows(result.topups)
      setTotal(result.total)
      setTotals(result.totals)
    } catch {
      setError('Could not load top-ups.')
    } finally {
      setLoading(false)
    }
  }, [filter, status, page])

  useEffect(() => {
    void load()
  }, [load])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const attempted = totals.successful + totals.failed

  return (
    <>
      <StatRow>
        <StatCard
          label="Revenue collected"
          value={formatINR(totals.revenue_collected)}
          color="#15803D"
          hint="What customers actually paid the gateway"
        />
        <StatCard
          label="Promotional bonus issued"
          value={formatINR(totals.promotional_bonus_issued)}
          color="#B45309"
          hint="The discount we funded"
        />
        <StatCard
          label="Wallet credited"
          value={formatINR(totals.wallet_credited)}
          hint="Revenue + bonus"
        />
        <StatCard
          label="Success rate"
          value={attempted > 0 ? `${Math.round((totals.successful / attempted) * 100)}%` : '-'}
          hint={`${totals.successful} paid · ${totals.failed} failed`}
        />
      </StatRow>

      <Card padded={false}>
        <div
          style={{
            display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap',
            padding: '16px', borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Field label="Status">
            <SelectInput value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All top-ups</option>
              <option value="paid">Successful</option>
              <option value="failed">Failed</option>
              <option value="created">Started, not completed</option>
            </SelectInput>
          </Field>
          <PillButton variant="secondary" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </PillButton>
        </div>

        {error ? (
          <div style={{ padding: '20px' }}><LoadErrorState message={error} onRetry={load} /></div>
        ) : rows.length === 0 && !loading ? (
          <div style={{ padding: '20px' }}>
            <EmptyState title="No top-ups" description="Nobody added money in this period." />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
              <TableHead columns={COLUMNS} />
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <Td>
                      <div>{row.customer_name || '-'}</div>
                      <div style={{ fontSize: '12px', color: colors.textMuted }}>{row.customer_phone}</div>
                    </Td>
                    <Td muted>{row.offer_code || 'Custom amount'}</Td>
                    <Td align="right" strong>{formatINR(row.pay_amount)}</Td>
                    <Td align="right" style={{ color: '#B45309' }}>
                      {Number(row.bonus_amount) > 0 ? `+ ${formatINR(row.bonus_amount)}` : '-'}
                    </Td>
                    <Td align="right" strong>{formatINR(row.credit_amount)}</Td>
                    <Td>
                      <Chip tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Chip>
                      {row.failure_reason ? (
                        <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px', maxWidth: '200px' }}>
                          {row.failure_reason}
                        </div>
                      ) : null}
                    </Td>
                    <Td muted>
                      {new Date(row.paid_at || row.created_at).toLocaleString('en-IN', {
                        day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
                      })}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 ? (
          <div
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 16px', borderTop: `1px solid ${colors.border}`,
            }}
          >
            <span style={{ fontSize: '13px', color: colors.textMuted }}>
              Page {page} of {totalPages} · {total} top-ups
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <PillButton variant="secondary" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
                Previous
              </PillButton>
              <PillButton variant="secondary" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
                Next
              </PillButton>
            </div>
          </div>
        ) : null}
      </Card>
    </>
  )
}
